import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { MessageService } from 'primeng/api';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/services/auth.service';
import { ApolloService } from 'src/services/apollo.service';
import { AuthenticationMutations } from 'src/graphql/mutations/authentication.mutations';
import { convertGraphqlUserDataOutputToUserData } from 'src/graphql/mappers/user-data.mappers';
import { GraphqlUserDataOutput } from 'src/graphql/models/user-data.output.model';
import { ExternalSessionService } from 'src/services/external-session.service';
import { ExternalModeService } from 'src/services/external-mode.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [MessageService],
})
export class AppComponent implements OnInit {
	private destroyRef = inject(DestroyRef);

	params$: Observable<ParamMap> = this.route.queryParamMap;

	navigationEnded$ = this.router.events.pipe(filter(e => e instanceof NavigationEnd));

	public isProcessingAuthentication = signal<boolean>(false);

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		protected authService: AuthService,
		private apolloService: ApolloService,

		private messageService: MessageService,

		private externalSessionService: ExternalSessionService,
		public externalModeService: ExternalModeService,
	) {}

	ngOnInit(): void {
		// Redirect old HashLocationStrategy URLs (e.g. /#/path?q=1) to PathLocationStrategy equivalents
		const hash = window.location.hash;
		if (hash.startsWith('#/')) {
			const hashContent = hash.slice(1); // e.g. '/path?q=1'
			this.router.navigateByUrl(hashContent, { replaceUrl: true });
			return;
		}

		combineLatest({
			userData: this.authService.userData$,
			navigationEnded: this.navigationEnded$.pipe(take(1)),
		})
			.pipe(
				map(source => source.userData),
				distinctUntilChanged((prev, curr) => {
					// Treat all falsy emissions as equal.
					if (!curr.sessionID && !prev.sessionID) {
						return true;
					}
					return prev.sessionID === curr.sessionID;
				}),
				exhaustMap(data => {
					if (!data.sessionID) {
						// If no session ID, clear external session.
						this.externalSessionService.clearSession();

						const token = this.route.snapshot.queryParamMap.get(environment.oneTimeTokenName);
						if (token) {
							if (token === 'noAuth') {
								this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to sign in. No authentication token provided..' });
								return of(false);
							} else {
								this.isProcessingAuthentication.set(true);
								return this.apolloService.authLayerClient.pipe(
									take(1),
									switchMap(client =>
										client.mutate<{ consumeOneTimeToken: GraphqlUserDataOutput }>({
											mutation: AuthenticationMutations.consumeOneTimeToken,
											variables: { token: token },
										}),
									),
									map(result => (result.data?.consumeOneTimeToken ? convertGraphqlUserDataOutputToUserData(result.data.consumeOneTimeToken) : undefined)),
									map(data => {
										if (data?.sessionID) {
											this.authService.updateSession(data);
											return false;
										} else {
											this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve user data. Please sign in again.' });
											return false;
										}
									}),
									catchError(() => {
										this.isProcessingAuthentication.set(false);
										this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to process sign in. Please sign in again.' });
										return of(false);
									}),
								);
							}
						} else {
							return of(true);
						}
					}
					return of(false);
				}),
				tap(() => this.authService.removeSpecificQueryParams(['sessionId', 'username', 'company', environment.oneTimeTokenName])),
				tap(() => this.isProcessingAuthentication.set(false)),

				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		this.params$
			.pipe(
				mergeMap(params => {
					const code = params.get('code');
					if (code) {
						return this.externalSessionService.exchangeCodeForTokens$(code).pipe(
							tap(token => {
								if (token) {
									this.externalModeService.activateExternalMode();
								}
							}),
							tap(() => this.authService.removeSpecificQueryParams(['code', 'state'])),
						);
					} else {
						return EMPTY;
					}
				}),
			)
			.subscribe();
	}
}
